/**
 * Production-Safe Logging System
 *
 * Batched error tracking with minimal performance overhead
 * Collects render errors and sends to BugSnag in production
 */

declare global {
  interface Window {
    __renderErrors?: Array<{
      component: string
      type: 'infinite-loop' | 'slow-render' | 'error'
      timestamp: number
      metadata?: Record<string, any>
    }>
    __loggerInitialized?: boolean
    Bugsnag?: {
      notify: (error: Error, callback?: (event: any) => void) => void
    }
  }
}

/**
 * Initialize production logger (call once in root layout)
 */
export function initProductionLogger() {
  if (typeof window === 'undefined') return
  if (window.__loggerInitialized) return

  // Initialize error queue
  window.__renderErrors = []
  window.__loggerInitialized = true

  const isDevelopment = process.env.NODE_ENV === 'development'

  // In development, just log immediately (no batching)
  if (isDevelopment) {
    console.log('[Logger] Production logger initialized (development mode - no batching)')
    return
  }

  // Production: Batch send every 5 seconds
  const BATCH_INTERVAL = 5000

  const sendBatch = () => {
    if (!window.__renderErrors || window.__renderErrors.length === 0) return

    const errors = [...window.__renderErrors]
    window.__renderErrors = []

    // Group by component
    const grouped = errors.reduce(
      (acc, error) => {
        if (!acc[error.component]) {
          acc[error.component] = []
        }
        acc[error.component].push(error)
        return acc
      },
      {} as Record<string, typeof errors>
    )

    // Send to error tracking
    Object.entries(grouped).forEach(([component, componentErrors]) => {
      const infiniteLoops = componentErrors.filter((e) => e.type === 'infinite-loop')
      const slowRenders = componentErrors.filter((e) => e.type === 'slow-render')
      const otherErrors = componentErrors.filter((e) => e.type === 'error')

      // Report infinite loops (critical)
      if (infiniteLoops.length > 0) {
        const firstError = infiniteLoops[0]
        if (window.Bugsnag) {
          window.Bugsnag.notify(new Error(`Infinite loop in ${component}`), (event) => {
            event.severity = 'error'
            event.addMetadata('infinite-loop', {
              component,
              occurrences: infiniteLoops.length,
              firstOccurrence: firstError.timestamp,
              lastOccurrence: infiniteLoops[infiniteLoops.length - 1].timestamp,
              metadata: firstError.metadata,
            })
          })
        }
      }

      // Report slow renders (info level, if more than 5)
      if (slowRenders.length > 5) {
        const avgDuration =
          slowRenders.reduce((sum, e) => sum + (e.metadata?.duration || 0), 0) / slowRenders.length

        if (window.Bugsnag) {
          window.Bugsnag.notify(new Error(`Slow renders in ${component}`), (event) => {
            event.severity = 'info'
            event.addMetadata('slow-render', {
              component,
              occurrences: slowRenders.length,
              averageDuration: avgDuration,
              batchWindow: BATCH_INTERVAL,
            })
          })
        }
      }

      // Report other errors
      if (otherErrors.length > 0) {
        otherErrors.forEach((error) => {
          if (window.Bugsnag) {
            window.Bugsnag.notify(new Error(`Error in ${component}`), (event) => {
              event.severity = 'warning'
              event.addMetadata('component-error', {
                component,
                timestamp: error.timestamp,
                metadata: error.metadata,
              })
            })
          }
        })
      }
    })
  }

  // Start batch interval
  const intervalId = setInterval(sendBatch, BATCH_INTERVAL)

  // Send remaining errors on page unload
  window.addEventListener('beforeunload', () => {
    clearInterval(intervalId)
    sendBatch()
  })

  console.log('[Logger] Production logger initialized (batching every 5s)')
}

/**
 * Log infinite loop detection
 */
export function logInfiniteLoop(
  component: string,
  renderCount: number,
  elapsed: number,
  metadata?: Record<string, any>
) {
  if (typeof window === 'undefined' || !window.__renderErrors) return

  window.__renderErrors.push({
    component,
    type: 'infinite-loop',
    timestamp: Date.now(),
    metadata: {
      renderCount,
      elapsed,
      ...metadata,
    },
  })
}

/**
 * Log slow render
 */
export function logSlowRender(component: string, duration: number, metadata?: Record<string, any>) {
  if (typeof window === 'undefined' || !window.__renderErrors) return

  window.__renderErrors.push({
    component,
    type: 'slow-render',
    timestamp: Date.now(),
    metadata: {
      duration,
      ...metadata,
    },
  })
}

/**
 * Log general component error
 */
export function logComponentError(
  component: string,
  error: Error | string,
  metadata?: Record<string, any>
) {
  if (typeof window === 'undefined' || !window.__renderErrors) return

  window.__renderErrors.push({
    component,
    type: 'error',
    timestamp: Date.now(),
    metadata: {
      error: typeof error === 'string' ? error : error.message,
      stack: typeof error === 'object' ? error.stack : undefined,
      ...metadata,
    },
  })
}

/**
 * Get current error queue size (for debugging)
 */
export function getErrorQueueSize(): number {
  if (typeof window === 'undefined' || !window.__renderErrors) return 0
  return window.__renderErrors.length
}

/**
 * Clear error queue (for debugging)
 */
export function clearErrorQueue() {
  if (typeof window !== 'undefined' && window.__renderErrors) {
    window.__renderErrors = []
  }
}
